import { useState } from 'react';
import { cn } from '@/lib/cn';
import {
  useAdminAuthorities,
  useCreateAuthority,
  useUpdateAuthority,
  useDeleteAuthority,
  useArchiveAuthority,
  useUnarchiveAuthority,
  useCreateAuthorityService,
  useUpdateAuthorityService,
  useDeleteAuthorityService,
  useArchiveAuthorityService,
  useUnarchiveAuthorityService,
  useBulkImportAuthorities,
} from '@/hooks/use-admin';
import { useSectionPermission } from '@/hooks/use-admin-permissions';
import type { AuthorityRow, AuthorityServiceRow, BulkImportAuthorityItem, InlineAuthorityService } from '@shared/types/api';

import { DownloadTemplateButton } from './authorities/download-template-button';
import { AuthorityModal } from './authorities/authority-modal';
import { ServiceModal } from './authorities/service-modal';
import { DeleteConfirmModal } from './authorities/delete-confirm-modal';
import { ArchiveConfirmModal } from './authorities/archive-confirm-modal';
import { ExcelUploadModal } from './authorities/excel-upload-modal';
import { AuthorityImageModal } from './authorities/authority-image-modal';

export function AdminAuthoritiesPage() {
  const { canCreate, canEdit, canDelete, isSuperAdmin } = useSectionPermission('AUTHORITIES');
  const { data: authorities = [], isLoading, error } = useAdminAuthorities();

  const createAuthMutation = useCreateAuthority();
  const updateAuthMutation = useUpdateAuthority();
  const deleteAuthMutation = useDeleteAuthority();
  const archiveAuthMutation = useArchiveAuthority();
  const unarchiveAuthMutation = useUnarchiveAuthority();

  const createSvcMutation = useCreateAuthorityService();
  const updateSvcMutation = useUpdateAuthorityService();
  const deleteSvcMutation = useDeleteAuthorityService();
  const archiveSvcMutation = useArchiveAuthorityService();
  const unarchiveSvcMutation = useUnarchiveAuthorityService();

  const bulkImportMutation = useBulkImportAuthorities();

  // Search & Tab filters
  const [search, setSearch] = useState('');
  const [tabFilter, setTabFilter] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');

  // Modals state
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [editingAuth, setEditingAuth] = useState<AuthorityRow | null>(null);
  const [authForImage, setAuthForImage] = useState<AuthorityRow | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const [svcModalOpen, setSvcModalOpen] = useState(false);
  const [selectedAuthIdForSvc, setSelectedAuthIdForSvc] = useState<string>('');
  const [editingSvc, setEditingSvc] = useState<AuthorityServiceRow | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'authority' | 'service';
    id: string;
    name: string;
  } | null>(null);

  const [archiveTarget, setArchiveTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [excelModalOpen, setExcelModalOpen] = useState(false);

  // Expanded Authorities (IDs)
  const [expandedAuthIds, setExpandedAuthIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedAuthIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Authority Handlers
  const handleOpenAddAuth = () => {
    setEditingAuth(null);
    setAuthModalOpen(true);
  };

  const handleOpenEditAuth = (auth: AuthorityRow) => {
    setEditingAuth(auth);
    setAuthModalOpen(true);
  };

  const handleSaveAuth = async (data: { name: string; code?: string | null; images?: string[]; services?: InlineAuthorityService[] }) => {
    if (editingAuth) {
      await updateAuthMutation.mutateAsync({
        id: editingAuth.id,
        payload: { name: data.name, code: data.code, images: data.images },
      });
    } else {
      await createAuthMutation.mutateAsync({
        name: data.name,
        code: data.code,
        images: data.images,
        services: data.services,
      });
    }
    setAuthModalOpen(false);
  };

  // Service Handlers
  const handleOpenAddSvc = (authorityId: string) => {
    setSelectedAuthIdForSvc(authorityId);
    setEditingSvc(null);
    setSvcModalOpen(true);
  };

  const handleOpenEditSvc = (svc: AuthorityServiceRow) => {
    setSelectedAuthIdForSvc(svc.authorityId);
    setEditingSvc(svc);
    setSvcModalOpen(true);
  };

  const handleSaveSvc = async (data: { authorityId?: string; code: string; name: string }) => {
    if (editingSvc) {
      await updateSvcMutation.mutateAsync({
        serviceId: editingSvc.id,
        payload: { code: data.code, name: data.name },
      });
    } else {
      await createSvcMutation.mutateAsync({
        authorityId: data.authorityId || selectedAuthIdForSvc,
        code: data.code,
        name: data.name,
      });
    }
    setSvcModalOpen(false);
  };

  // Delete Handler
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'authority') {
      await deleteAuthMutation.mutateAsync(deleteTarget.id);
    } else {
      await deleteSvcMutation.mutateAsync(deleteTarget.id);
    }
    setDeleteTarget(null);
  };

  // Archive Handler
  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    await archiveAuthMutation.mutateAsync(archiveTarget.id);
    setArchiveTarget(null);
  };

  // Excel Bulk Import Handler
  const handleBulkImport = async (items: BulkImportAuthorityItem[]) => {
    await bulkImportMutation.mutateAsync({ items });
  };

  // Filter authorities by search term & tab filter
  const filteredAuthorities = authorities.filter((auth) => {
    if (tabFilter === 'ACTIVE' && auth.isArchived) return false;
    if (tabFilter === 'ARCHIVED' && !auth.isArchived && !auth.services.some((s) => s.isArchived)) return false;

    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const matchesAuth =
      auth.name.toLowerCase().includes(q) || (auth.code && auth.code.toLowerCase().includes(q));
    const matchesSvc = auth.services.some(
      (s) => s.code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q),
    );
    return matchesAuth || matchesSvc;
  });

  const activeAuthoritiesCount = authorities.filter((a) => !a.isArchived).length;
  const archivedAuthoritiesCount = authorities.filter((a) => a.isArchived).length;

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Authority & Services</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage government authorities, service IDs, and service names.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DownloadTemplateButton />

          {(canCreate || isSuperAdmin) && (
            <>
              <button
                type="button"
                onClick={() => setExcelModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-emerald-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                Excel Upload
              </button>

              <button
                type="button"
                onClick={handleOpenAddAuth}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Authority
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="mt-4 mb-6 flex items-center overflow-x-auto no-scrollbar scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-gray-200 gap-2">
        <button
          type="button"
          onClick={() => setTabFilter('ACTIVE')}
          className={cn(
            'shrink-0 flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
            tabFilter === 'ACTIVE'
              ? 'border-brand-600 text-brand-800 font-semibold'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
          )}
        >
          <span>Active Authorities</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              tabFilter === 'ACTIVE'
                ? 'bg-brand-100 text-brand-800'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {activeAuthoritiesCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setTabFilter('ARCHIVED')}
          className={cn(
            'shrink-0 flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
            tabFilter === 'ARCHIVED'
              ? 'border-slate-600 text-slate-800 font-semibold'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
          )}
        >
          <span>Archived Authorities</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              tabFilter === 'ARCHIVED'
                ? 'bg-slate-200 text-slate-800'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {archivedAuthoritiesCount}
          </span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search authority, code or service name..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
      </div>

      {/* Content Area */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center rounded-xl border border-gray-200 bg-white p-8">
          <div className="flex flex-col items-center gap-3 text-gray-500">
            <svg className="h-8 w-8 animate-spin text-brand-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm font-medium">Loading Authorities & Services...</span>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          <p className="font-semibold">Failed to load authorities.</p>
          <p className="mt-1 text-sm">{(error as any).message}</p>
        </div>
      ) : filteredAuthorities.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-700">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="21" x2="21" y2="21" />
              <line x1="6" y1="18" x2="6" y2="11" />
              <line x1="10" y1="18" x2="10" y2="11" />
              <line x1="14" y1="18" x2="14" y2="11" />
              <line x1="18" y1="18" x2="18" y2="11" />
              <polygon points="12 3 2 8 22 8 12 3" />
            </svg>
          </div>
          <h3 className="mt-4 text-base font-semibold text-gray-900">No authorities found</h3>
          <p className="mt-1 max-w-sm text-sm text-gray-500">
            {search
              ? 'No results matched your search query.'
              : tabFilter === 'ARCHIVED'
              ? 'No archived authorities.'
              : 'Get started by creating your first authority or uploading via Excel using the buttons above.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAuthorities.map((auth) => {
            const isExpanded = expandedAuthIds.has(auth.id) || search.trim().length > 0;
            return (
              <div
                key={auth.id}
                className={`overflow-hidden rounded-xl border transition-all duration-200 ${
                  auth.isArchived
                    ? 'border-slate-300 bg-slate-100/50'
                    : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'
                }`}
              >
                {/* Authority Header Bar */}
                <div
                  className={`flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between border-b ${
                    auth.isArchived ? 'bg-slate-200/60 border-slate-300' : 'bg-gray-50/50 border-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => toggleExpand(auth.id)}>
                    <button
                      type="button"
                      className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                      aria-label="Toggle services list"
                    >
                      <svg
                        className={`h-4 w-4 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="flex items-center gap-3">
                      {auth.images && auth.images.length > 0 ? (
                        <div className="h-12 w-12 rounded-lg overflow-hidden border border-gray-200 bg-white p-1 shrink-0 shadow-xs flex items-center justify-center">
                          <img src={auth.images[0]} alt={auth.name} className="h-full w-full object-contain" />
                        </div>
                      ) : null}
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className={`text-base font-semibold ${auth.isArchived ? 'text-slate-600 line-through' : 'text-gray-900'}`}>
                            {auth.name}
                          </h2>
                          {auth.code && (
                            <span className="rounded bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 border border-brand-200">
                              {auth.code}
                            </span>
                          )}
                          {auth.isArchived && (
                            <span className="rounded-md bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700 border border-slate-300">
                              Archived
                            </span>
                          )}
                          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            {auth.services.length} {auth.services.length === 1 ? 'service' : 'services'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 pl-8 sm:pl-0">
                    {!auth.isArchived && (
                      <>
                        {(canEdit || isSuperAdmin) && (
                          <button
                            type="button"
                            onClick={() => {
                              setAuthForImage(auth);
                              setImageModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 cursor-pointer"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                            Image
                          </button>
                        )}
                        {(canCreate || isSuperAdmin) && (
                          <button
                            type="button"
                            onClick={() => handleOpenAddSvc(auth.id)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-brand-700 bg-white px-2.5 py-1.5 text-xs font-medium text-brand-700 shadow-sm hover:bg-brand-50 cursor-pointer"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Add Service
                          </button>
                        )}
                      </>
                    )}

                    {(canEdit || isSuperAdmin) ? (
                      <button
                        type="button"
                        onClick={() => handleOpenEditAuth(auth)}
                        className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      >
                        Edit
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpenEditAuth(auth)}
                        className="inline-flex items-center rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                      >
                        View
                      </button>
                    )}

                    {/* Archive / Unarchive Button */}
                    {(canDelete || isSuperAdmin) && (
                      auth.isArchived ? (
                        <button
                          type="button"
                          onClick={() => unarchiveAuthMutation.mutate(auth.id)}
                          disabled={unarchiveAuthMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 8v13H3V8" />
                            <path d="M1 3h22v5H1z" />
                            <path d="M10 12h4" />
                          </svg>
                          Unarchive
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setArchiveTarget({ id: auth.id, name: auth.name })}
                          disabled={archiveAuthMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 8v13H3V8" />
                            <path d="M1 3h22v5H1z" />
                            <path d="M10 12h4" />
                          </svg>
                          Archive
                        </button>
                      )
                    )}

                    {(canDelete || isSuperAdmin) && (
                      <button
                        type="button"
                        onClick={() =>
                          setDeleteTarget({
                            type: 'authority',
                            id: auth.id,
                            name: auth.name,
                          })
                        }
                        className="rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 shadow-sm hover:bg-red-50 hover:border-red-300"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>

                {/* Services Table */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {auth.services.length === 0 ? (
                      <div className="p-6 text-center text-xs text-gray-400">
                        No services registered under this authority.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[500px] text-left text-xs">
                          <thead className="bg-gray-50/75 text-gray-500 uppercase tracking-wider font-semibold border-b border-gray-100">
                            <tr>
                              <th className="py-2.5 pl-6 pr-4 w-44 whitespace-nowrap">Service ID</th>
                              <th className="py-2.5 px-4 whitespace-nowrap">Service Name</th>
                              <th className="py-2.5 pr-6 pl-4 text-right whitespace-nowrap">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 text-gray-700">
                            {auth.services
                              .filter((svc) => {
                                if (tabFilter === 'ACTIVE') return !svc.isArchived;
                                if (tabFilter === 'ARCHIVED' && !auth.isArchived) return svc.isArchived;
                                return true;
                              })
                              .map((svc) => (
                              <tr
                                key={svc.id}
                                className={`transition-colors ${
                                  svc.isArchived ? 'bg-slate-100/50 text-slate-500' : 'hover:bg-gray-50/50'
                                }`}
                              >
                                <td className="py-3 pl-6 pr-4 font-mono font-semibold">
                                  <span className={svc.isArchived ? 'line-through text-slate-400' : 'text-brand-700'}>
                                    {svc.code}
                                  </span>
                                  {svc.isArchived && (
                                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 border border-slate-300">
                                      Archived
                                    </span>
                                  )}
                                </td>
                                <td className={`py-3 px-4 font-medium ${svc.isArchived ? 'line-through text-slate-400' : 'text-gray-900'}`}>
                                  {svc.name}
                                </td>
                                <td className="py-3 pr-6 pl-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    {(canEdit || isSuperAdmin) && !svc.isArchived ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => handleOpenEditSvc(svc)}
                                          className="font-medium text-brand-600 hover:text-brand-800"
                                        >
                                          Edit
                                        </button>
                                        {(canDelete || isSuperAdmin) && <span className="text-gray-300">|</span>}
                                      </>
                                    ) : !svc.isArchived ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => handleOpenEditSvc(svc)}
                                          className="inline-flex items-center rounded border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                                        >
                                          View
                                        </button>
                                        {(canDelete || isSuperAdmin) && <span className="text-gray-300">|</span>}
                                      </>
                                    ) : null}

                                    {(canDelete || isSuperAdmin) && (
                                      svc.isArchived ? (
                                        <button
                                          type="button"
                                          onClick={() => unarchiveSvcMutation.mutate(svc.id)}
                                          disabled={unarchiveSvcMutation.isPending}
                                          className="font-semibold text-slate-700 hover:text-slate-900"
                                        >
                                          Unarchive
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => archiveSvcMutation.mutate(svc.id)}
                                          disabled={archiveSvcMutation.isPending}
                                          className="font-medium text-gray-600 hover:text-gray-900"
                                        >
                                          Archive
                                        </button>
                                      )
                                    )}

                                    {(canDelete || isSuperAdmin) && (
                                      <>
                                        <span className="text-gray-300">|</span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setDeleteTarget({
                                              type: 'service',
                                              id: svc.id,
                                              name: `${svc.code} - ${svc.name}`,
                                            })
                                          }
                                          className="font-medium text-red-600 hover:text-red-800"
                                        >
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Authority Modal with Fixed Sticky Bottom Footer */}
      <AuthorityModal
        key={editingAuth ? `edit-${editingAuth.id}` : `new-auth-${authModalOpen}`}
        editingAuth={editingAuth}
        isOpen={authModalOpen}
        readOnly={!canEdit && !isSuperAdmin}
        isSaving={createAuthMutation.isPending || updateAuthMutation.isPending}
        onClose={() => setAuthModalOpen(false)}
        onSave={handleSaveAuth}
      />

      {/* Service Modal */}
      <ServiceModal
        key={editingSvc ? `edit-svc-${editingSvc.id}` : `new-svc-${selectedAuthIdForSvc}`}
        authorities={authorities.filter((a) => !a.isArchived)}
        editingSvc={editingSvc}
        isOpen={svcModalOpen}
        readOnly={!canEdit && !isSuperAdmin}
        isSaving={createSvcMutation.isPending || updateSvcMutation.isPending}
        selectedAuthId={selectedAuthIdForSvc}
        onClose={() => setSvcModalOpen(false)}
        onSave={handleSaveSvc}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        deleteTarget={deleteTarget}
        isDeleting={deleteAuthMutation.isPending || deleteSvcMutation.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      {/* Archive Confirmation Modal */}
      <ArchiveConfirmModal
        archiveTarget={archiveTarget}
        isArchiving={archiveAuthMutation.isPending}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleConfirmArchive}
      />

      {/* Excel Upload Modal */}
      <ExcelUploadModal
        isOpen={excelModalOpen}
        isImporting={bulkImportMutation.isPending}
        onClose={() => setExcelModalOpen(false)}
        onImport={handleBulkImport}
      />

      {/* Authority Image Modal */}
      <AuthorityImageModal
        key={authForImage ? `img-${authForImage.id}` : 'new-img-modal'}
        authority={authForImage}
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
      />
    </div>
  );
}
